/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Settings,
  Save,
  Globe,
  Mail,
  MapPin,
  Palette,
  Share2,
  BarChart,
  Server,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const [formData, setFormData] = useState({
    siteName: 'FAJTech',
    tagline: 'Premier Managed IT & Cybersecurity Solutions',
    logoUrl: '/static/logo121.png',
    faviconUrl: '/favicon.ico',
    primaryColor: '#2563eb',
    accentColor: '#0f172a',
    contactEmail: 'info@fajtech.com',
    contactPhone: '+1 (800) 555-0199',
    address: '123 Tech Boulevard, Suite 400, Innovation City, NY 10001',
    googleMapsEmbed: '',
    socialLinks: JSON.stringify({
      linkedin: 'https://linkedin.com/company/fajtech',
      twitter: 'https://twitter.com/fajtech',
      facebook: 'https://facebook.com/fajtech',
      github: 'https://github.com/fajtech',
    }, null, 2),
    analyticsId: 'G-FAJTECH123',
    smtpHost: 'mail.fajtech.com',
    smtpPort: 587,
    smtpUser: 'notifications@fajtech.com',
    smtpPass: '',
  });

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'contact' | 'social' | 'smtp'

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axiosClient.get('/settings');
      if (res.data) {
        setFormData(prev => ({
          ...prev,
          ...res.data,
          socialLinks: typeof res.data.socialLinks === 'object'
            ? JSON.stringify(res.data.socialLinks, null, 2)
            : res.data.socialLinks || prev.socialLinks,
        }));
      }
    } catch (err) {
      toast.error('Failed to load site settings.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.put('/settings', formData);
      toast.success('Site settings updated successfully!');
    } catch (err) {
      toast.error('Failed to update site settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <Settings className="text-blue-500" />
            <span>Global Site Settings</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Configure website branding, contact information, social links, analytics, and SMTP mailer.
          </p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-5 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all self-start sm:self-auto"
        >
          <Save size={16} />
          <span>{saving ? 'Saving...' : 'Save All Settings'}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2 border-b border-slate-800 pb-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all whitespace-nowrap ${
            activeTab === 'general'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Globe size={15} />
          <span>Brand & Styling</span>
        </button>
        <button
          onClick={() => setActiveTab('contact')}
          className={`px-4 py-2 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all whitespace-nowrap ${
            activeTab === 'contact'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Mail size={15} />
          <span>Contact & Maps</span>
        </button>
        <button
          onClick={() => setActiveTab('social')}
          className={`px-4 py-2 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all whitespace-nowrap ${
            activeTab === 'social'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Share2 size={15} />
          <span>Social Profiles</span>
        </button>
        <button
          onClick={() => setActiveTab('smtp')}
          className={`px-4 py-2 font-bold text-xs rounded-xl flex items-center space-x-2 transition-all whitespace-nowrap ${
            activeTab === 'smtp'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200'
          }`}
        >
          <Server size={15} />
          <span>SMTP & Analytics</span>
        </button>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="space-y-6 text-xs">
        {/* Tab 1: General & Brand */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <Globe className="text-blue-400" size={18} />
                <h3 className="font-bold text-sm text-white">Identity Details</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Website Name / Brand Title</label>
                <input
                  type="text"
                  value={formData.siteName || ''}
                  onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Brand Tagline</label>
                <input
                  type="text"
                  value={formData.tagline || ''}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Logo Asset URL</label>
                <input
                  type="text"
                  value={formData.logoUrl || ''}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  placeholder="/static/logo121.png"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Favicon Asset URL</label>
                <input
                  type="text"
                  value={formData.faviconUrl || ''}
                  onChange={(e) => setFormData({ ...formData, faviconUrl: e.target.value })}
                  placeholder="/favicon.ico"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <Palette className="text-purple-400" size={18} />
                <h3 className="font-bold text-sm text-white">Brand Palette</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Primary Theme Color</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.primaryColor || '#2563eb'}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="w-10 h-10 rounded-xl bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primaryColor || '#2563eb'}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="flex-1 px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Accent / Neutral Color</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.accentColor || '#0f172a'}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    className="w-10 h-10 rounded-xl bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.accentColor || '#0f172a'}
                    onChange={(e) => setFormData({ ...formData, accentColor: e.target.value })}
                    className="flex-1 px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white font-mono"
                  />
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <span className="font-bold text-slate-300 block">Theme Preview</span>
                <div className="flex items-center space-x-3">
                  <div
                    className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md"
                    style={{ backgroundColor: formData.primaryColor }}
                  >
                    Primary Button
                  </div>
                  <div
                    className="px-4 py-2 rounded-xl text-slate-200 font-bold text-xs border border-slate-700"
                    style={{ backgroundColor: formData.accentColor }}
                  >
                    Dark Surface
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Contact & Maps */}
        {activeTab === 'contact' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <Mail className="text-blue-400" size={18} />
                <h3 className="font-bold text-sm text-white">Contact Info</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Public Contact Email</label>
                <input
                  type="email"
                  value={formData.contactEmail || ''}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={formData.contactPhone || ''}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Headquarters Physical Address</label>
                <textarea
                  rows={3}
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <MapPin className="text-rose-400" size={18} />
                <h3 className="font-bold text-sm text-white">Google Maps Embed</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Embed iframe or Map URL</label>
                <textarea
                  rows={6}
                  value={formData.googleMapsEmbed || ''}
                  onChange={(e) => setFormData({ ...formData, googleMapsEmbed: e.target.value })}
                  placeholder="https://maps.google.com/..."
                  className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Social Profiles */}
        {activeTab === 'social' && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
              <Share2 className="text-cyan-400" size={18} />
              <h3 className="font-bold text-sm text-white">Social Media Links (JSON / URL Map)</h3>
            </div>

            <div>
              <label className="block font-semibold text-slate-300 mb-1">Social Accounts JSON Payload</label>
              <textarea
                rows={8}
                value={formData.socialLinks || ''}
                onChange={(e) => setFormData({ ...formData, socialLinks: e.target.value })}
                className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-cyan-400 font-mono text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Tab 4: SMTP & Analytics */}
        {activeTab === 'smtp' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <Server className="text-emerald-400" size={18} />
                <h3 className="font-bold text-sm text-white">SMTP Email Gateway</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">SMTP Host</label>
                <input
                  type="text"
                  value={formData.smtpHost || ''}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                  placeholder="mail.fajtech.com"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white font-mono focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold text-slate-300 mb-1">SMTP Port</label>
                  <input
                    type="number"
                    value={formData.smtpPort || 587}
                    onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white font-mono focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-300 mb-1">SMTP User</label>
                  <input
                    type="text"
                    value={formData.smtpUser || ''}
                    onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">SMTP Password</label>
                <input
                  type="password"
                  value={formData.smtpPass || ''}
                  onChange={(e) => setFormData({ ...formData, smtpPass: e.target.value })}
                  placeholder="••••••••••••"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center space-x-2.5 pb-3 border-b border-slate-800">
                <BarChart className="text-amber-400" size={18} />
                <h3 className="font-bold text-sm text-white">Analytics Integration</h3>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Google Analytics Measurement ID / GTM Tag</label>
                <input
                  type="text"
                  value={formData.analyticsId || ''}
                  onChange={(e) => setFormData({ ...formData, analyticsId: e.target.value })}
                  placeholder="G-XXXXXXXXXX"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white font-mono focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
